import { useEffect, useRef, useState } from 'react';
import { api, ApiError, type Ubicacion } from './api';
import { useMarca } from './marca';

type DocEditor = { destroyEditor: () => void };
type DocsApi = { DocEditor: new (id: string, config: Record<string, unknown>) => DocEditor };

declare global {
  interface Window {
    DocsAPI?: DocsApi;
  }
}

/** Carga el script del Document Server una sola vez, aunque se remonte el componente. */
function cargarApi(documentServerUrl: string): Promise<DocsApi> {
  if (window.DocsAPI) return Promise.resolve(window.DocsAPI);

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${documentServerUrl}/web-apps/apps/api/documents/api.js`;
    script.onload = () =>
      window.DocsAPI ? resolve(window.DocsAPI) : reject(new Error('El editor no se inicializó.'));
    script.onerror = () => reject(new Error('No se pudo conectar con el servidor de documentos.'));
    document.head.appendChild(script);
  });
}

export function Editor({ ubicacion }: { ubicacion: Ubicacion }) {
  const tituloApp = useMarca().titulo;
  const [error, setError] = useState<string | null>(null);
  const [titulo, setTitulo] = useState('Abriendo…');
  const [modo, setModo] = useState<'edicion' | 'lectura' | null>(null);
  const [listo, setListo] = useState(false);
  const editor = useRef<DocEditor | null>(null);

  useEffect(() => {
    let cancelado = false;

    (async () => {
      try {
        const { documentServerUrl, modo, config } = await api.officeConfig(ubicacion);
        const docs = await cargarApi(documentServerUrl);
        if (cancelado) return;

        const doc = config.document as { title?: string } | undefined;
        setTitulo(doc?.title ?? 'Documento');
        setModo(modo);
        document.title = `${doc?.title ?? 'Documento'} · ${tituloApp}`;

        editor.current = new docs.DocEditor('editor-onlyoffice', {
          ...config,
          width: '100%',
          height: '100%',
          events: {
            onDocumentReady: () => setListo(true),
            onError: (e: { data?: { errorDescription?: string } }) =>
              setError(e.data?.errorDescription ?? 'El editor informó un error.'),
          },
        });
      } catch (err) {
        if (!cancelado) {
          setError(
            err instanceof ApiError || err instanceof Error
              ? err.message
              : 'No pudimos abrir el documento.',
          );
        }
      }
    })();

    return () => {
      cancelado = true;
      editor.current?.destroyEditor();
      editor.current = null;
    };
  }, [ubicacion]);

  return (
    <div className="editor-pagina">
      <header className="editor-barra">
        <a href="/" className="editor-volver">
          ← Volver a Cloud
        </a>
        <span className="editor-titulo">{titulo}</span>
        {modo && !listo && <span className="chip">Cargando documento…</span>}
        {modo && listo && (
          <span className={modo === 'edicion' ? 'chip activo' : 'chip'} data-listo="true">
            {modo === 'edicion' ? 'Edición' : 'Solo lectura'}
          </span>
        )}
      </header>

      {error ? (
        <div className="centro">
          <div className="panel">
            <h2>No se pudo abrir el documento</h2>
            <p className="error">{error}</p>
            <a href="/">Volver a Cloud</a>
          </div>
        </div>
      ) : (
        <div className="editor-marco">
          <div id="editor-onlyoffice" />
        </div>
      )}
    </div>
  );
}
